import { Component, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { PAGINATION_SIZES } from 'src/app/shared/utils/constants';
import { OrderDto } from 'src/app/shared/models/order/order-dto';
import { OrderService } from 'src/app/shared/services/order/order.service';
import { formatIsoDate } from 'src/app/shared/utils/functions';
import { StateDto } from 'src/app/shared/models/location/state-dto';
import { LocationService } from 'src/app/shared/services/location/location.service';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { UserLoginResponseDto } from 'src/app/shared/models/auth/auth-dto';

@Component({
    selector: 'app-list-order',
    templateUrl: './list-order.component.html',
    standalone: false
})
export class ListOrderComponent implements OnInit {
  @ViewChild('modalConfirmCancel') modalConfirmCancel: ModalComponent;
  @ViewChild('contentModalConfirmCancel') contentModalConfirmCancel: TemplateRef<HTMLElement>;
  selectedItem: OrderDto;
  loading = false;
  states: StateDto[] = [];
  userData: UserLoginResponseDto = null;

  tableConfig: TableConfig<OrderDto> = {
    paginator: {
      pageSize: 10,
      position: 'bottom',
      pageSizeOptions: PAGINATION_SIZES
    },
    columns: {
      codeOrder: { header: 'Cód. Pedido', cell: 'codeCell', sortable: true },
      codeOrderClient: { header: 'Cód. Pedido Cliente', sortable: true, cell: 'codeOrderClientCell' },
      date: { header: 'Data Pedido', sortable: true, cell: (item) => formatIsoDate(item.date) },
      dateRegister: { header: 'Data Cadastro Pedido', sortable: true, cell: (item) => formatIsoDate(item.dateRegister) },
      automakerCompanyTradeName: {
        header: 'Montadora (OE)',
        sortable: true,
        cell: (item) => item.automaker?.company?.tradeName,
        sortKey: 'automaker.company.tradeName'
      },
      dealershipCompanyTradeName: {
        header: 'Concessionária (CC)',
        sortable: true,
        cell: (item) => item.dealership?.company?.tradeName,
        sortKey: 'dealership.company.tradeName'
      },
      distributorCompanyTradeName: {
        header: 'Dist. Exclusivo (DE)',
        sortable: true,
        cell: 'distributor',
        sortKey: 'distributor.company.tradeName'
      },
      distributorCompanyAddressCityStateCode: {
        header: 'UF (DE)',
        sortable: true,
        cell: (item) => item.distributor?.company?.address?.city?.state?.code,
        sortKey: 'distributor.company.address.city.state.code'
      },
      percent: { header: 'Andamento', cell: (item) => item.percent },
      status: { header: 'Status', sortable: false, cell: (item) => this.statusMap[item.status] || '' },
      actions: { header: '  ', cell: 'actionsCell', sortable: false },
      cancel: { header: '  ', cell: 'cancelCell', sortable: false }
    }
  };

  filterForm: FormGroup = this.form.group({
    code: this.form.control('', { nonNullable: true }),
    dateFrom: this.form.control<Date | null>(null),
    dateTo: this.form.control<Date | null>(null),
    automakerCode: this.form.control('', { nonNullable: true }),
    automakerTradeName: this.form.control('', { nonNullable: true }),
    dealershipCode: this.form.control('', { nonNullable: true }),
    dealershipTradeName: this.form.control('', { nonNullable: true }),
    dealershipDocument: this.form.control('', { nonNullable: true }),
    orderCode: this.form.control('', { nonNullable: true }),
    distributorTradeName: this.form.control('', { nonNullable: true }),
    state: this.form.control('', { nonNullable: true }),
    status: this.form.control('', { nonNullable: true })
  });

  stateCode = (state: StateDto): string => state && `${state.name} (${state.code})`;

  fetcher: PaginatorFetcher<OrderDto> = (filters: PaginatorFilters) => this.orderService.search(filters);

  @ViewChild(TableComponent, { static: true }) table: TableComponent<OrderDto>;

  constructor(
    private readonly router: Router,
    private readonly form: FormBuilder,
    private readonly orderService: OrderService,
    private readonly locationService: LocationService,
    private readonly alertService: AlertService,
    private readonly authService: AuthService
  ) {}

  get state(): FormControl {
    return this.filterForm.get('state') as FormControl;
  }

  get filterValues(): any {
    return {
      ...this.filterForm.value,
      stateCode: this.filterForm.value.state?.code
    };
  }

  ngOnInit() {
    this.loading = true;
    this.locationService
      .findAllStates()
      .subscribe({
        next: (result) => (this.states = result),
        error: () => this.alertService.showAlert({ type: 'critical', title: 'Ocorreu um erro ao recuperar as informações' } as Alert)
      })
      .add(() => (this.loading = false));
  }

  applyFilter() {
    this.table.patchExtraFilters(this.filterValues, true);
  }

  resetFilter() {
    this.filterForm.reset();
    this.table.patchExtraFilters(this.filterValues, true);
  }

  redirectToDetails(item: OrderDto) {
    this.router.navigate([`/orders/${item.id}`]);
  }

  cancelItem(item: OrderDto) {
    this.selectedItem = item;
    this.modalConfirmCancel.open({
      id: 'modalConfirmCancel',
      width: 768,
      title: 'Cancelamento de Pedido',
      content: this.contentModalConfirmCancel,
      cancel: { text: 'Cancelar', icon: 'close' },
      confirm: { text: 'Confirmar', icon: 'done', color: 'warn' }
    });
  }

  onModalConfirmCancelClosed(result: ModalResult): void {
    if (result.result) {
      this.loading = true;
      this.orderService
        .cancelOrder(this.selectedItem?.id)
        .subscribe({
          next: () => {
            this.showAlert(AlertType.success, 'Pedido cancelado com sucesso!');
            this.applyFilter();
          },
          error: () =>
            this.alertService.showAlert({
              type: AlertType.strong,
              content: 'Erro ao cancelar o pedido.',
              timestamp: 5000
            } as Alert)
        })
        .add(() => (this.loading = false));
    }
  }

  showAlert(type: AlertType, content: string) {
    this.alertService.showAlert({
      type: type,
      content: content
    } as Alert);
  }

  isDisabled(item: any): boolean {
    this.userData = this.authService.getUserData();
    let authorize = false;

    if (!item?.automaker) {
      return true;
    }

    switch (this.userData.userProfile.userType) {
      case 'AUTOMAKER':
        if (item.automaker.orderCancellationAutomaker) {
          authorize = true;
        }
        break;
      case 'DISTRIBUTOR':
        if (item.automaker.orderCancellationDistributor) {
          authorize = true;
        }
        break;
      case 'MICHELIN':
        if (item.automaker.orderCancellationMichelin) {
          authorize = true;
        }
        break;
      default:
        authorize = false;
        break;
    }
    return !authorize || item.percent !== '0%' || item.status === 'C' || item.status === 'R' || item.status === 'F';
  }

  statusMap: Record<string, string> = {
    O: 'Em Aberto',
    P: 'Em Andamento',
    F: 'Finalizado',
    R: 'Recusado',
    C: 'Cancelado'
  };
}
